import {DateTimeFormatter, LocalDate} from '@js-joda/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import React, {ReactElement, useMemo} from 'react';
import InlineLoadingIndicator from "../../LoadingIndicator/InlineLoadingIndicator";
import {MergedCapacityWithAverages, useCapacityContext} from "../Utils/CapacityContextProvider";
import {Holiday, ProjectSchedule, ProjectScheduleWeek, Week} from '../Utils/EmployeeSchedulesCompiler';

const useStyles = makeStyles(theme => ({
    totals: {
        backgroundColor: theme.custom.tableFooter,
        '& td': {
            color: theme.palette.text.primary,
            fontWeight: 500,
        },
    },
    input: {
        color: 'inherit',
        border: 'none',
        backgroundColor: 'transparent',
        margin: 0,
        padding: 0,
        width: 30,
        textAlign: 'right',
    },
}));

type Props = {
    projectSchedules : ProjectSchedule[];
    employeeId : number;
    label : string | ReactElement ;
    weeks : Week[];
    timeOff : ProjectScheduleWeek[];
};

const calculateSuggestedWeeklyCapacity = (
    weeks : Week[],
    mergedCapacityWithAverages : MergedCapacityWithAverages,
    holidays : Holiday[],
    timeOffWeeks : ProjectScheduleWeek[]
) => {
    const firstMonth = weeks[0].startDate.month().value() + 1;
    const fiscalMonthCapacityPerDay = mergedCapacityWithAverages.fiscalMonthCapacityPerDay;

    return weeks.map(week => {
        const startingMonth = week.startDate.month().value() + 1;
        const startingMonthIndex = (startingMonth < firstMonth ? 12 + startingMonth : startingMonth) - firstMonth;
        const nextMonthIndex = startingMonthIndex + 1;
        const foundTimeOff = timeOffWeeks
            .find(timeOffWeek => LocalDate.parse(timeOffWeek.startOfWeek).isEqual(week.startDate));

        let daysInCurrentMonth = 0
        let daysInNextMonth = 0

        for (let i = 1; i <= 5; i++) {
            const checkDate = week.startDate.plusDays(i);
            const foundHoliday = holidays.find(holiday => {
                const holidayDate = LocalDate.parse(holiday.HolidayDate, DateTimeFormatter.ofPattern('M/d/yyyy'));
                return holidayDate.isEqual(checkDate)
            })

            if (foundHoliday) {
                continue;
            }

            const checkMonth = checkDate.month().value() + 1;

            if (checkMonth === startingMonth) {
                daysInCurrentMonth++;
            } else {
                daysInNextMonth++;
            }
        }

        if (foundTimeOff && foundTimeOff.scheduled > 0) {
            const daysOff = (foundTimeOff.scheduled / 8);
            if (daysOff > daysInCurrentMonth) {
                daysInNextMonth = daysInCurrentMonth + daysInNextMonth - daysOff;
                daysInCurrentMonth = 0;
            } else {
                daysInCurrentMonth = daysInCurrentMonth - daysOff;
            }
        }

        const currentMonthTime = fiscalMonthCapacityPerDay[startingMonthIndex] * daysInCurrentMonth;
        const nextMonthTime = fiscalMonthCapacityPerDay[nextMonthIndex] * daysInNextMonth;

        let totalTime = currentMonthTime + nextMonthTime;

        if (totalTime < 0) {
            totalTime = 0;
        }
        return Math.round(totalTime);
    })
}

const Capacity : React.FC<Props> = ({projectSchedules, employeeId, label, weeks, timeOff} : Props) => {
    const classes = useStyles();
    const {holidays, capacityData} = useCapacityContext();

    const userCapacityData = useMemo(() :  MergedCapacityWithAverages | undefined => {
        if (capacityData) {
            return capacityData.find(data => data.associateID === employeeId) ?? {
                associateID : employeeId,
                fiscalMonthReservedCapacity : [0,0,0,0,0,0,0],
                fiscalMonthRemainingCapacity : [150,150,150,150,150,150,150,150],
                fiscalMonthCapacityPerDay : [8,8,8,8,8,8,8,8],
            };
        }
        return undefined;
    }, [capacityData, employeeId]);

    const suggestedWeeklyCapacity = useMemo(() => {
        if (userCapacityData && holidays) {
            return calculateSuggestedWeeklyCapacity(weeks, userCapacityData, holidays, timeOff);
        }
    }, [holidays, timeOff, userCapacityData, weeks]);

    return <>
        <TableRow className={classes.totals}>
            <TableCell>{label}</TableCell>

            {!(userCapacityData && suggestedWeeklyCapacity) ? (
                <TableCell colSpan={8}>
                    <InlineLoadingIndicator size={'1em'} noPadding={true}/>
                </TableCell>
            ) : suggestedWeeklyCapacity.map((total, index) => (
                <TableCell key={`suggested-capacity-${index}`}>
                    <input type="text" disabled={true} maxLength={2} className={classes.input} value={total.toFixed(0)}/>
                </TableCell>
            ))}
        </TableRow>
    </>;
};

export default Capacity;
